import React, { useEffect, useState } from 'react';
import { clinicService } from '../services/clinicService';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const { register: registerAppointment, handleSubmit: handleAppointmentSubmit, reset: resetAppointment, setValue: setAppointmentValue, formState: { errors: appointmentErrors } } = useForm();

  useEffect(() => {
    loadPatients();
  }, [currentPage, searchTerm]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await clinicService.getPatients({ page: currentPage, search: searchTerm });
      setPatients(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      await clinicService.createPatient(data);
      toast.success('Patient registered successfully!');
      setShowModal(false);
      reset();
      loadPatients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register patient');
    }
  };

  const handleView = (patient) => {
    setSelectedPatient(patient);
    setShowViewModal(true);
  };

  const handleViewHistory = async (patient) => {
    setSelectedPatient(patient);
    try {
      const response = await clinicService.getAppointments({ patient_id: patient.id, per_page: 100 });
      setPatientAppointments(response.data || []);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to load appointment history');
    }
  };

  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentValue('notes', appointment.notes || '');
    setAppointmentValue('diagnosis', appointment.diagnosis || '');
    setAppointmentValue('prescription', appointment.prescription || '');
    setAppointmentValue('amount_paid', appointment.amount_paid || '');
    setAppointmentValue('payment_status', appointment.payment_status || 'unpaid');
    setShowEditAppointmentModal(true);
  };

  const onAppointmentEditSubmit = async (data) => {
    try {
      await clinicService.updateAppointment(selectedAppointment.id, {
        notes: data.notes,
        diagnosis: data.diagnosis,
        prescription: data.prescription,
        amount_paid: data.amount_paid,
        payment_status: data.payment_status
      });
      toast.success('Appointment updated successfully!');
      setShowEditAppointmentModal(false);
      resetAppointment();
      setSelectedAppointment(null);
      // Reload appointment history
      if (selectedPatient) {
        const response = await clinicService.getAppointments({ patient_id: selectedPatient.id, per_page: 100 });
        setPatientAppointments(response.data || []);
      }
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getStatusBadge = (status) => {
    const badges = {
      booked: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const handleEdit = (patient) => {
    setSelectedPatient(patient);
    setValue('name', patient.name);
    setValue('email', patient.email || '');
    setValue('mobile', patient.mobile);
    setValue('dob', patient.dob || '');
    setValue('gender', patient.gender);
    setValue('blood_group', patient.blood_group || '');
    setValue('address', patient.address || '');
    setValue('medical_history', patient.medical_history || '');
    setValue('allergies', patient.allergies || '');
    setValue('notes', patient.notes || '');
    setShowEditModal(true);
  };

  const onEditSubmit = async (data) => {
    try {
      await clinicService.updatePatient(selectedPatient.id, data);
      toast.success('Patient updated successfully!');
      setShowEditModal(false);
      reset();
      setSelectedPatient(null);
      loadPatients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update patient');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await clinicService.deletePatient(id);
        toast.success('Patient deleted successfully');
        loadPatients();
      } catch (error) {
        toast.error('Failed to delete patient');
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (loading && patients.length === 0) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">👥 Patients</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
        >
          ➕ Register Patient
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="🔍 Search by name, code, mobile, or email..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full max-w-md px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blood Group</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {patients.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  No patients found. Register your first patient!
                </td>
              </tr>
            ) : (
              patients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">{patient.patient_code}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{patient.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{patient.mobile}</td>
                  <td className="px-6 py-4 whitespace-nowrap capitalize">{patient.gender || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{patient.blood_group || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleView(patient)}
                      className="text-green-600 hover:text-green-800 mr-3 font-medium"
                    >
                      👁️ View
                    </button>
                    <button
                      onClick={() => handleViewHistory(patient)}
                      className="text-purple-600 hover:text-purple-800 mr-3 font-medium"
                    >
                      📋 History
                    </button>
                    <button
                      onClick={() => handleEdit(patient)}
                      className="text-blue-600 hover:text-blue-800 mr-3 font-medium"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(patient.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} patients
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 border rounded-lg bg-blue-50 text-blue-600 font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
              disabled={currentPage === pagination.last_page}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Patient Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Register New Patient</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input {...register('name', { required: 'Name is required' })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.name && <span className="text-red-500 text-xs">{errors.name.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile *</label>
                  <input {...register('mobile', { required: 'Mobile is required' })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.mobile && <span className="text-red-500 text-xs">{errors.mobile.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" {...register('email')} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date of Birth</label>
                  <input type="date" {...register('dob')} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Gender *</label>
                  <select {...register('gender', { required: 'Gender is required' })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && <span className="text-red-500 text-xs">{errors.gender.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Blood Group</label>
                  <select {...register('blood_group')} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <textarea {...register('address')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Medical History</label>
                  <textarea {...register('medical_history')} className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Previous diseases, surgeries, etc." />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Allergies</label>
                  <textarea {...register('allergies')} className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Drug allergies, food allergies, etc." />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Additional Notes</label>
                  <textarea {...register('notes')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold">
                  Register Patient
                </button>
                <button type="button" onClick={() => { setShowModal(false); reset(); }} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Patient Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Edit Patient</h2>
            <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input {...register('name', { required: 'Name is required' })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile *</label>
                  <input {...register('mobile', { required: 'Mobile is required' })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" {...register('email')} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date of Birth</label>
                  <input type="date" {...register('dob')} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Gender *</label>
                  <select {...register('gender', { required: 'Gender is required' })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Blood Group</label>
                  <select {...register('blood_group')} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <textarea {...register('address')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Medical History</label>
                  <textarea {...register('medical_history')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Allergies</label>
                  <textarea {...register('allergies')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Additional Notes</label>
                  <textarea {...register('notes')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold">
                  Update Patient
                </button>
                <button type="button" onClick={() => { setShowEditModal(false); reset(); setSelectedPatient(null); }} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appointment History Modal */}
      {showHistoryModal && selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Appointment History - {selectedPatient.name}</h2>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            {patientAppointments.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No appointments found for this patient.</p>
            ) : (
              <div className="space-y-4">
                {patientAppointments.map((appointment) => {
                  const startTime = formatDateTime(appointment.slot_start);
                  const endTime = formatDateTime(appointment.slot_end);
                  return (
                    <div key={appointment.id} className="border rounded-lg p-6 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-blue-600">{appointment.appointment_number}</h3>
                          <p className="text-sm text-gray-600">{startTime.date} at {startTime.time} - {endTime.time}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(appointment.status)}`}>
                            {appointment.status}
                          </span>
                          {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                            <button
                              onClick={() => handleEditAppointment(appointment)}
                              className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-medium transition"
                            >
                              ✏️ Edit
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Doctor</label>
                          <p className="font-medium">{appointment.doctor?.name}</p>
                          <p className="text-sm text-gray-600">{appointment.doctor?.specialization}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Consultation Fee</label>
                          <p className="font-medium">₹{appointment.doctor?.consultation_fee}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Amount Paid</label>
                          <p className="font-medium text-green-600">₹{appointment.amount_paid || '0.00'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Payment Status</label>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                            appointment.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            appointment.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {appointment.payment_status || 'unpaid'}
                          </span>
                        </div>
                      </div>
                      {appointment.notes && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-500">Chief Complaint / Notes</label>
                          <p className="text-sm bg-gray-50 p-3 rounded">{appointment.notes}</p>
                        </div>
                      )}
                      {appointment.diagnosis && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-500">Diagnosis</label>
                          <p className="text-sm bg-blue-50 p-3 rounded">{appointment.diagnosis}</p>
                        </div>
                      )}
                      {appointment.prescription && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-500">Prescription</label>
                          <p className="text-sm bg-green-50 p-3 rounded whitespace-pre-wrap">{appointment.prescription}</p>
                        </div>
                      )}
                      {!appointment.diagnosis && !appointment.prescription && appointment.status === 'booked' && (
                        <p className="text-sm text-gray-400 italic">Appointment pending - No diagnosis or prescription yet</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Patient Modal */}
      {showViewModal && selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Patient Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Patient Code</label>
                  <p className="text-lg font-semibold text-blue-600">{selectedPatient.patient_code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Name</label>
                  <p className="text-lg font-semibold">{selectedPatient.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Mobile</label>
                  <p className="text-lg">{selectedPatient.mobile}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Email</label>
                  <p className="text-lg">{selectedPatient.email || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
                  <p className="text-lg">{selectedPatient.dob || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Gender</label>
                  <p className="text-lg capitalize">{selectedPatient.gender || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Blood Group</label>
                  <p className="text-lg">{selectedPatient.blood_group || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Registered On</label>
                  <p className="text-lg">{new Date(selectedPatient.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {selectedPatient.address && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Address</label>
                  <p className="text-lg">{selectedPatient.address}</p>
                </div>
              )}
              {selectedPatient.medical_history && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Medical History</label>
                  <p className="text-lg bg-gray-50 p-3 rounded">{selectedPatient.medical_history}</p>
                </div>
              )}
              {selectedPatient.allergies && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Allergies</label>
                  <p className="text-lg bg-red-50 p-3 rounded text-red-700">{selectedPatient.allergies}</p>
                </div>
              )}
              {selectedPatient.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Additional Notes</label>
                  <p className="text-lg bg-gray-50 p-3 rounded">{selectedPatient.notes}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => { setShowViewModal(false); handleEdit(selectedPatient); }} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold">
                Edit Patient
              </button>
              <button onClick={() => setShowViewModal(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Appointment Modal */}
      {showEditAppointmentModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Update Appointment</h2>
            
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">Appointment #</label>
                  <p className="font-semibold">{selectedAppointment.appointment_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Patient</label>
                  <p className="font-semibold">{selectedAppointment.patient?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Doctor</label>
                  <p className="font-semibold">Dr. {selectedAppointment.doctor?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(selectedAppointment.status)}`}>
                    {selectedAppointment.status}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleAppointmentSubmit(onAppointmentEditSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Chief Complaint / Notes</label>
                <textarea
                  {...registerAppointment('notes')}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Patient's chief complaint or notes"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Diagnosis</label>
                <textarea
                  {...registerAppointment('diagnosis')}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Doctor's diagnosis"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prescription</label>
                <textarea
                  {...registerAppointment('prescription')}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="4"
                  placeholder="Prescribed medications and instructions"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount Paid (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...registerAppointment('amount_paid')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Status</label>
                  <select
                    {...registerAppointment('payment_status')}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold"
                >
                  💾 Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditAppointmentModal(false);
                    resetAppointment();
                    setSelectedAppointment(null);
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Patients;
